import { useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, Clock, XCircle, Home, Phone } from "lucide-react";
import { KioskViewport, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const AUTO_RETURN_DELAY = 15000;

export default function KioskConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { success, requiresReview, registrationData, error } = location.state || {};
  const timerRef = useRef(null);

  const getStatus = () => {
    if (error) return 'error';
    if (requiresReview) return 'pending';
    return 'success';
  };

  const status = getStatus();
  
  // Build contextual confirmation message based on status
  const confirmationMessage = useMemo(() => {
    if (status === 'success') {
      return "Congratulations! Your registration is now complete. Your vehicle has been added to our whitelist. You may now use designated parking spaces. Thank you for using ParkWise.";
    } else if (status === 'pending') {
      return "Your registration has been submitted for manual review. Our team will verify your details and you will receive an email confirmation once approved.";
    } else {
      return "Sorry, your registration could not be completed. Please try again or speak to a member of staff for assistance.";
    }
  }, [status]);
  
  // Central speech controller - speak confirmation message
  useKioskSpeech('KioskConfirmation', confirmationMessage, true);

  useEffect(() => {
    // Auto-return to home after delay
    timerRef.current = setTimeout(() => {
      navigate('/kiosk');
    }, AUTO_RETURN_DELAY);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate]);

  const statusConfig = {
    success: {
      icon: CheckCircle,
      iconColor: '#22c55e',
      iconBg: '#dcfce7',
      title: 'Registration Complete!',
      message: 'Your designated parking registration has been approved. You can now use the designated parking spaces.',
      subMessage: 'A confirmation email has been sent to your email address.'
    },
    pending: {
      icon: Clock,
      iconColor: '#f59e0b',
      iconBg: '#fef3c7',
      title: 'Submitted for Review',
      message: 'Your registration has been submitted and is pending manual review. We will email you once it has been processed.',
      subMessage: 'This usually takes 1-2 working days.'
    },
    error: {
      icon: XCircle,
      iconColor: '#ef4444',
      iconBg: '#fef2f2',
      title: 'Registration Failed',
      message: 'We were unable to complete your registration. Please try again or speak to a staff member.',
      subMessage: null
    }
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-confirmation"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#ffffff'
        }}
      >
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 40px', background: 'white', flexShrink: 0
        }}>
          <img src="/parkwise-logo.png" alt="ParkWise" style={{ height: '60px', objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon size={32} color={config.iconColor} />
            <span style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a5f' }}>{config.title}</span>
          </div>
        </header>

        {/* HERO SECTION */}
        <div style={{ position: 'relative', height: '600px', overflow: 'hidden', flexShrink: 0 }}>
          <img src="/cityscape.png" alt="Background" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }} />
          <img src="/tracy.png" alt="Tracy" style={{ position: 'absolute', right: '-20px', bottom: '-40px', height: '680px', zIndex: 10 }} />
          
          <div style={{
            position: 'absolute', top: '40px', left: '40px', width: '480px',
            background: '#fef9e7', borderRadius: '24px', padding: '28px 32px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 20
          }}>
            <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#1e3a5f', margin: '0 0 16px 0' }}>
              {status === 'success' ? 'Thank you!' : status === 'pending' ? 'Almost there!' : 'We apologise'}
            </h3>
            <p style={{ fontSize: '20px', color: '#374151', lineHeight: 1.5, margin: 0 }}>
              {config.message}
            </p>
            <div style={{ position: 'absolute', right: '-22px', top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '18px solid transparent', borderBottom: '18px solid transparent', borderLeft: '24px solid #fef9e7' }} />
          </div>
        </div>

        {/* RESULT SECTION */}
        <div style={{
          flex: 1, padding: '40px', background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '32px'
        }}>
          {/* Status Icon */}
          <div style={{
            width: '200px', height: '200px', borderRadius: '50%',
            background: config.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 16px 64px rgba(0,0,0,0.1)'
          }}>
            <Icon size={100} color={config.iconColor} />
          </div>

          {/* Status Text */}
          <div style={{ textAlign: 'center', maxWidth: '700px' }}>
            <h2 style={{ fontSize: '40px', fontWeight: '700', color: '#1e3a5f', margin: '0 0 16px 0' }}>
              {config.title}
            </h2>
            <p style={{ fontSize: '22px', color: '#475569', lineHeight: 1.5, margin: 0 }}>
              {config.message}
            </p>
            {config.subMessage && (
              <p style={{ fontSize: '18px', color: '#64748b', marginTop: '16px' }}>
                {config.subMessage}
              </p>
            )}
          </div>

          {/* Registration Details */}
          {registrationData && (
            <div style={{
              background: 'white', borderRadius: '20px', padding: '24px 32px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)', width: '100%', maxWidth: '600px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '18px', color: '#64748b' }}>Vehicle:</span>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#1e3a5f' }}>
                  {registrationData?.vehicle_registration || 'N/A'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '18px', color: '#64748b' }}>Email:</span>
                <span style={{ fontSize: '18px', color: '#1e3a5f' }}>
                  {registrationData?.email || 'N/A'}
                </span>
              </div>
            </div>
          )}

          {/* Return Home Button */}
          <button
            onClick={() => navigate('/kiosk')}
            style={{
              display: 'flex', alignItems: 'center', gap: '16px',
              padding: '24px 64px', borderRadius: '20px', border: 'none',
              background: '#3b82f6', cursor: 'pointer',
              fontSize: '28px', fontWeight: '700', color: 'white',
              boxShadow: '0 8px 32px rgba(59,130,246,0.3)'
            }}
            data-testid="return-home-button"
          >
            <Home size={32} /> Return to Home
          </button>

          <p style={{ fontSize: '16px', color: '#64748b' }}>
            Returning to home screen automatically in 15 seconds...
          </p>
        </div>

        {/* HELP INFO - In content area, no footer */}
        <div style={{
          margin: '24px 40px',
          padding: '20px 32px',
          background: '#1e3a5f',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <Phone size={24} color="#fbbf24" />
          <span style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>Need help? Call </span>
          <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>01292 833107</span>
        </div>
      </div>
    </KioskViewport>
  );
}
